/**
 * Notification Service
 * Handles browser notifications and push notification subscriptions
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

class NotificationService {
  constructor() {
    this.swRegistration = null;
    this.pushSubscription = null;
    this.permissionGranted = false;
  }

  /**
   * Initialize the notification service
   * - Register service worker
   * - Request notification permission
   * - Subscribe to push notifications
   */
  async init(token) {
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.log('[Notifications] Not supported in this browser');
        return false;
      }

      // Register service worker
      if ('serviceWorker' in navigator) {
        this.swRegistration = await navigator.serviceWorker.register('/sw.js');
        console.log('[Notifications] Service Worker registered');
        
        // Listen for messages from service worker
        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage);
      }

      // Request notification permission
      const permission = await this.requestPermission();
      if (!permission) {
        console.log('[Notifications] Permission denied');
        return false;
      }

      // Subscribe to push notifications
      if (token && this.swRegistration) {
        await this.subscribeToPush(token);
      }

      return true;
    } catch (error) {
      console.error('[Notifications] Init failed:', error);
      return false;
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
      return true;
    }

    if (Notification.permission === 'denied') {
      console.log('[Notifications] Permission previously denied');
      return false;
    }

    const permission = await Notification.requestPermission();
    this.permissionGranted = permission === 'granted';
    return this.permissionGranted;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPush(token) {
    try {
      if (!this.swRegistration) {
        console.log('[Notifications] No service worker registration');
        return false;
      }

      // Get VAPID public key from server
      const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
      if (!response.ok) {
        console.log('[Notifications] Failed to get VAPID key');
        return false;
      }

      const { publicKey } = await response.json();
      
      // Convert VAPID key to Uint8Array
      const vapidKey = this.urlBase64ToUint8Array(publicKey);

      // Subscribe to push
      this.pushSubscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey
      });

      console.log('[Notifications] Push subscription created');

      // Send subscription to server
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: this.pushSubscription.toJSON()
        })
      });

      console.log('[Notifications] Push subscription registered with server');
      return true;
    } catch (error) {
      console.error('[Notifications] Push subscription failed:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(token) {
    try {
      if (this.pushSubscription) {
        const endpoint = this.pushSubscription.endpoint;
        await this.pushSubscription.unsubscribe();
        
        // Notify server
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ endpoint })
        });
        
        this.pushSubscription = null;
        console.log('[Notifications] Unsubscribed from push');
      }
    } catch (error) {
      console.error('[Notifications] Unsubscribe failed:', error);
    }
  }

  /**
   * Show a browser notification (when app is in foreground)
   */
  showNotification(title, options = {}) {
    if (!this.permissionGranted) {
      console.log('[Notifications] Permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      requireInteraction: false,
      silent: false
    };

    const notification = new Notification(title, {
      ...defaultOptions,
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) {
        options.onClick();
      }
    };

    return notification;
  }

  /**
   * Show incoming call notification
   */
  showIncomingCallNotification(callerName, callerNumber, onAnswer) {
    // Skip browser notification if running in Electron - window auto-shows instead
    if (window.electronAPI?.isElectron) {
      console.log('[Notifications] Running in Electron - skipping browser notification (window auto-shows)');
      return null;
    }

    // Skip if push subscription is active - service worker will show the notification via push
    if (this.pushSubscription) {
      console.log('[Notifications] Push subscription active - service worker will handle notification');
      return null;
    }

    if (!this.permissionGranted) {
      console.log('[Notifications] Permission not granted for incoming call');
      return null;
    }

    const displayName = callerName || callerNumber;
    
    const notification = new Notification('Incoming Call', {
      body: `${displayName}\nClick here or open CONNECT to answer`,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'incoming-call',
      requireInteraction: true,
      silent: false, // Allow system sound
      vibrate: [200, 100, 200, 100, 200]
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (onAnswer) {
        onAnswer();
      }
    };

    return notification;
  }

  /**
   * Close a notification by tag
   */
  closeNotification(tag) {
    if (this.swRegistration) {
      this.swRegistration.getNotifications({ tag }).then(notifications => {
        notifications.forEach(notification => notification.close());
      });
    }
  }

  /**
   * Handle messages from service worker
   */
  handleServiceWorkerMessage = (event) => {
    console.log('[Notifications] Service Worker message:', event.data);
    
    if (event.data?.type === 'NOTIFICATION_CLICK') {
      // Handle notification click from service worker
      const data = event.data.data;
      const action = event.data.action;
      
      if (data?.type === 'incoming_call') {
        // Focus the window
        window.focus();
        
        // If user clicked "Answer" button, dispatch custom event for auto-answer
        if (action === 'answer') {
          console.log('[Notifications] User clicked Answer - dispatching auto-answer event');
          window.dispatchEvent(new CustomEvent('auto-answer-call'));
        }
      }
    }
  };

  /**
   * Convert URL-safe base64 to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Check if notifications are supported and enabled
   */
  isSupported() {
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
